import { create } from 'zustand/react';
import { ImportAssortmentItemsState } from './types';
import {
  getIsRecognizedHeaderPredicate,
  recognizedPriorityHeadings,
  recognizedStockedStatusHeadings,
} from './utils';
import { ItemPriority, ProductGroupBase } from '@retail/products/types';
import dayjs from 'dayjs';
import {
  AssortmentItemImportResultDTO,
  AssortmentItemMinimal,
  AssortmentItemUpload,
} from '@retail/my-assortment/types';
import {
  recognizedMgItemNrHeadings,
  recognizedGtinHeadings,
  recognizedSupplierIdHeadings,
  recognizedValidFromHeadings,
  recognizedValidToHeadings,
  dateFromString,
  recognizedNobbNrHeadings,
  recognizedFinfoNrHeadings,
  recognizedSubAssortmentsHeadings,
} from '@retail/utils';

export const useImportAssortmentItemsStore = create<ImportAssortmentItemsState>((set) => ({
  importedAssortmentItems: undefined,
  importedFile: undefined,
  validItems: undefined,
  invalidItemsState: undefined,

  importSpreadsheet: (rows, file) => {
    const [headerRow, ...dataRows] = rows;
    const mgItemNumberColumnIndex = headerRow.findIndex((header) =>
      recognizedMgItemNrHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const gtinColumnIndex = headerRow.findIndex((header) =>
      recognizedGtinHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const nobbNumberColumnIndex = headerRow.findIndex((header) =>
      recognizedNobbNrHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const finfoNumberColumnIndex = headerRow.findIndex((header) =>
      recognizedFinfoNrHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const supplierIdColumnIndex = headerRow.findIndex((header) =>
      recognizedSupplierIdHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const priorityColumnIndex = headerRow.findIndex((header) =>
      recognizedPriorityHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const stockedStatusIndex = headerRow.findIndex((header) =>
      recognizedStockedStatusHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const validFromColumnIndex = headerRow.findIndex((header) =>
      recognizedValidFromHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );
    const validToColumnIndex = headerRow.findIndex((header) =>
      recognizedValidToHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );

    const subAssortmentsColumnIndex = headerRow.findIndex((header) =>
      recognizedSubAssortmentsHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
    );

    const updatedState = dataRows.map((row) => {
      const mgItemNumber =
        mgItemNumberColumnIndex !== -1 ? Number(row.at(mgItemNumberColumnIndex)) : undefined;
      const gtinCode = gtinColumnIndex !== -1 ? row.at(gtinColumnIndex) : undefined;
      const supplierId =
        supplierIdColumnIndex !== -1 ? Number(row.at(supplierIdColumnIndex)) : undefined;

      const nobbNumber =
        nobbNumberColumnIndex !== -1 ? Number(row.at(nobbNumberColumnIndex)) : undefined;
      const finfoNumber =
        finfoNumberColumnIndex !== -1 ? Number(row.at(finfoNumberColumnIndex)) : undefined;
      const priorityValue = priorityColumnIndex !== -1 ? row.at(priorityColumnIndex) : undefined;
      const priority =
        priorityValue && priorityValue.toString().toLowerCase() !== 'no'
          ? ItemPriority.TEN
          : ItemPriority.ZERO;

      const stockedStatusValue =
        stockedStatusIndex !== -1
          ? row.at(stockedStatusIndex)?.toString().toLowerCase()
          : undefined;
      const isStocked =
        stockedStatusValue === 'b' || stockedStatusValue === 'bestilling' ? false : true;

      const validFromValue = validFromColumnIndex !== -1 ? row.at(validFromColumnIndex) : undefined;

      const validFrom =
        validFromValue && new Date(validFromValue) >= new Date()
          ? dayjs(validFromValue).format('YYYY-MM-DD')
          : dayjs(new Date()).format('YYYY-MM-DD');

      const validToValue = validToColumnIndex !== -1 ? row.at(validToColumnIndex) : undefined;
      const validTo = validToValue ? dayjs(validToValue).format('YYYY-MM-DD') : undefined;

      const subAssortmentValue =
        subAssortmentsColumnIndex !== -1 ? row.at(subAssortmentsColumnIndex) : undefined;

      const subAssortmentIds =
        subAssortmentValue !== undefined && subAssortmentValue !== null
          ? subAssortmentValue
              .toString()
              .split(',')
              .map((id) => Number(id.trim()))
          : [];

      return {
        mgItemNumber,
        gtinCode,
        nobbNumber,
        finfoNumber,
        supplierId,
        priority,
        isStocked,
        validFrom,
        validTo,
        tagIds: subAssortmentIds,
      } as AssortmentItemUpload;
    }, []);

    set({ importedAssortmentItems: updatedState, importedFile: file });
  },
  setValidationState: (itemValidation: AssortmentItemImportResultDTO[]) => {
    const validItems = itemValidation
      .filter((itemVal) => !(itemVal.errorCode ?? []).includes('MG_ITEM_NOT_FOUND'))
      .map((itemVal) => itemVal.items)
      .flat()
      .map((item) => {
        const assortmentItemData = item.supplierItemPriorityData;
        const validFrom = assortmentItemData.importedValidFrom ?? assortmentItemData.validFrom;
        const validTo = assortmentItemData.importedValidTo ?? assortmentItemData.validTo;
        const priority = assortmentItemData.importedPriority ?? assortmentItemData.priority;
        return {
          ...item,
          priority: {
            prioritised: priority === ItemPriority.TEN,
            validFrom: dateFromString(validFrom),
            validTo: dateFromString(validTo),
          },
          validFrom: dateFromString(validFrom),
          validTo: dateFromString(validTo),
          assortmentTags: item.tags,
          supplier: { id: item.supplier.id, name: item.supplier.name },
          productGroup: ProductGroupBase.fromDto({
            name: item.productGroup.name,
            groupNumber: item.productGroup.groupNumber,
          }),
        };
      }) as AssortmentItemMinimal[];

    const uniqueItems = validItems.filter(
      (currentItem, index, self) =>
        index ===
        self.findIndex(
          (item) =>
            item.mgItemNumber === currentItem.mgItemNumber &&
            item.supplier.id === currentItem.supplier.id
        )
    );

    const invalidItemsState = itemValidation
      .map((item, index) => {
        return { ...item, index };
      })
      .filter((itemVal) => !!itemVal.errorCode);

    set({ validItems: uniqueItems, invalidItemsState });
  },
  clearState: () =>
    set({
      importedAssortmentItems: undefined,
      importedFile: undefined,
      validItems: undefined,
      invalidItemsState: undefined,
    }),
}));
